import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import type { BillingOrganisation } from './billing-profile-api.service';
import {
  BehaviorSubject,
  forkJoin,
  map,
  Observable,
  of,
  catchError,
} from 'rxjs';
import { SchoolApiService } from './school-api.service';
import { SchoolAuthorityApiService } from './school-authority-api.service';
import { UserSchoolItem, UserSchoolAuthorityItem } from '../types/rest-api';

export type UserRole = 'fachkraft' | 'schuleinheit' | 'schulgemeinde';

/** A school or authority by id, before it is matched against the user's lists. */
export type ContextOwner = { type: 'school' | 'authority'; id: string };

export type ActiveContext =
  | { type: 'school'; id: string; name: string }
  | { type: 'authority'; id: string; name: string };

/**
 * Access levels that carry authority over the whole organisation -- the tree a
 * Schulgemeinde bills for, or a standalone root unit.
 *
 * `AUTHORITY_SUPER_ADMIN` is a co-Super-Admin: someone holding an OWNER
 * permission row rather than being the accountable Besitzer. The backend used
 * to report them as `AUTHORITY_ADMIN`, so every check here treated them as an
 * ordinary invited admin -- which is why a second Super Admin saw an empty
 * Abonnement tab.
 */
const FULL_ORGANISATION_ACCESS: readonly string[] = [
  'OWNER',
  'AUTHORITY_OWNER',
  'AUTHORITY_SUPER_ADMIN',
];

/**
 * Access levels a *school* entry carries when the user reaches it through the
 * Schulgemeinde above it. Such a unit never bills for itself.
 */
const AUTHORITY_DERIVED_ACCESS: readonly string[] = [
  'AUTHORITY_OWNER',
  'AUTHORITY_SUPER_ADMIN',
  'AUTHORITY_ADMIN',
];

function hasFullOrganisationAccess(accessType: string | null): boolean {
  return accessType !== null && FULL_ORGANISATION_ACCESS.includes(accessType);
}

/** True when the user can administer the school authority (Schulgemeinde). */
function hasAuthorityAdminAccess(
  authorities: UserSchoolAuthorityItem[]
): boolean {
  return authorities.some(
    a =>
      // Backend can return `access_type: "OWNER"` for authority-level admins.
      // We must treat it as authority admin access; otherwise the UI will
      // incorrectly downgrade to `schuleinheit` when the school list also
      // contains an `OWNER` access record.
      hasFullOrganisationAccess(a.access_type) ||
      a.access_type === 'AUTHORITY_ADMIN'
  );
}

/** True when the user has school-unit admin access (Schuleinheit). */
function hasSchoolAdminAccess(schools: UserSchoolItem[]): boolean {
  return schools.some(s => {
    const t = s.access_type as string;
    return t === 'SCHOOL_ADMIN' || t === 'ROOT_SCHOOL_ADMIN' || t === 'OWNER';
  });
}

/**
 * Derive UI role from API lists. Previously any `authorities.length > 0` forced
 * `schulgemeinde`, so school admins with a Schulgemeinde link always saw the
 * org invite dialog instead of the Schuleinheit (Figma) flow.
 */
function resolveUserRole(
  schools: UserSchoolItem[],
  authorities: UserSchoolAuthorityItem[]
): UserRole {
  const authAdmin = hasAuthorityAdminAccess(authorities);
  const schoolAdmin = hasSchoolAdminAccess(schools);

  if (authAdmin && !schoolAdmin) {
    return 'schulgemeinde';
  }
  if (schoolAdmin && !authAdmin) {
    return 'schuleinheit';
  }
  if (authAdmin && schoolAdmin) {
    return 'schulgemeinde';
  }

  if (schools.length > 0 && authorities.length === 0) {
    return 'schuleinheit';
  }
  if (authorities.length > 0 && schools.length === 0) {
    return 'schulgemeinde';
  }
  if (schools.length > 0) {
    return 'schuleinheit';
  }
  if (authorities.length > 0) {
    return 'schulgemeinde';
  }

  return 'fachkraft';
}

/**
 * Tab-scoped copy of {@link RoleService.activeContext}, so pages whose URL names no
 * context (`/jobs`, `/chat`, …) keep it across reloads. Logout clears sessionStorage.
 */
const ACTIVE_CONTEXT_STORAGE_KEY = 'bn.activeContext';

function readStoredContext(): ActiveContext | null {
  try {
    const raw = sessionStorage.getItem(ACTIVE_CONTEXT_STORAGE_KEY);
    const ctx = raw ? (JSON.parse(raw) as ActiveContext) : null;
    return ctx &&
      (ctx.type === 'school' || ctx.type === 'authority') &&
      typeof ctx.id === 'string'
      ? ctx
      : null;
  } catch {
    return null;
  }
}

function writeStoredContext(ctx: ActiveContext | null): void {
  try {
    if (ctx) {
      sessionStorage.setItem(ACTIVE_CONTEXT_STORAGE_KEY, JSON.stringify(ctx));
    } else {
      sessionStorage.removeItem(ACTIVE_CONTEXT_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable: the context just won't survive a reload.
  }
}

@Injectable({
  providedIn: 'root',
})
export class RoleService {
  private readonly schoolApi = inject(SchoolApiService);
  private readonly schoolAuthorityApi = inject(SchoolAuthorityApiService);

  private _role = new BehaviorSubject<UserRole>('fachkraft');
  private _mySchools = new BehaviorSubject<UserSchoolItem[]>([]);
  private _myAuthorities = new BehaviorSubject<UserSchoolAuthorityItem[]>([]);
  private _activeContext = new BehaviorSubject<ActiveContext | null>(null);
  /** {@link activeContext} is `loadRole()`'s first-entry default, not a user or URL choice. */
  private _contextIsFallback = false;
  /** See {@link awaitPageContext}. */
  private _pageContextPending = false;
  private _pageOwner: ContextOwner | null = null;
  private readonly _awaitingPageSig = signal(false);
  /**
   * True from {@link awaitPageContext} until a context is published. The navbar hides
   * its switcher and context links meanwhile instead of showing its first-entry fallbacks.
   */
  readonly contextResolvingSignal: Signal<boolean> = computed(
    () => this._awaitingPageSig() && !this._activeContextSig()
  );
  private _loaded = false;

  readonly role$ = this._role.asObservable();
  readonly mySchools$ = this._mySchools.asObservable();
  readonly myAuthorities$ = this._myAuthorities.asObservable();
  readonly activeContext$ = this._activeContext.asObservable();

  /**
   * Signal mirrors of the state above.
   *
   * The observables predate signals and are still what most of the app
   * subscribes to, so they stay the transport. But a `computed()` reading the
   * plain `.value` getters has no producer to depend on: it evaluates once, on
   * the first change detection, and never again. A page that renders before
   * `loadRole()` resolves -- `/settings/delete-account` cold-loads exactly like
   * that -- then caches the empty picture permanently.
   *
   * The `publish*` helpers below are the only writers, so the two
   * representations cannot drift.
   */
  private readonly _roleSig = signal<UserRole>('fachkraft');
  private readonly _mySchoolsSig = signal<UserSchoolItem[]>([]);
  private readonly _myAuthoritiesSig = signal<UserSchoolAuthorityItem[]>([]);
  private readonly _activeContextSig = signal<ActiveContext | null>(null);

  readonly roleSignal: Signal<UserRole> = this._roleSig.asReadonly();
  readonly mySchoolsSignal: Signal<UserSchoolItem[]> =
    this._mySchoolsSig.asReadonly();
  readonly myAuthoritiesSignal: Signal<UserSchoolAuthorityItem[]> =
    this._myAuthoritiesSig.asReadonly();
  readonly activeContextSignal: Signal<ActiveContext | null> =
    this._activeContextSig.asReadonly();

  /** Reactive {@link billingOrganisation}. */
  readonly billingOrganisationSignal: Signal<BillingOrganisation | null> =
    computed(() => {
      // Read through the signals so this recomputes when the lists arrive.
      this._mySchoolsSig();
      this._myAuthoritiesSig();
      return this.billingOrganisationFor(this._activeContextSig());
    });

  /** Reactive {@link hasSubscriptionAccess}. */
  readonly hasSubscriptionAccessSignal: Signal<boolean> = computed(
    () => this.billingOrganisationSignal() !== null
  );

  private publishSchools(items: UserSchoolItem[]): void {
    this._mySchools.next(items);
    this._mySchoolsSig.set(items);
  }

  private publishAuthorities(items: UserSchoolAuthorityItem[]): void {
    this._myAuthorities.next(items);
    this._myAuthoritiesSig.set(items);
  }

  private publishRole(role: UserRole): void {
    this._role.next(role);
    this._roleSig.set(role);
  }

  /** Restored from sessionStorage; `loadRole()` checks it belongs to the signed-in user. */
  private _contextUnverified = false;

  constructor() {
    // Synchronously, so the navbar's first render already shows it.
    const stored = readStoredContext();
    if (stored) {
      this._contextUnverified = true;
      this.publishContext(stored);
    }
  }

  private publishContext(ctx: ActiveContext | null): void {
    if (ctx) this._awaitingPageSig.set(false);
    // Only persist real choices, never the first-entry default.
    writeStoredContext(ctx && !this._contextIsFallback ? ctx : null);
    this._activeContext.next(ctx);
    this._activeContextSig.set(ctx);
  }

  get role(): UserRole {
    return this._role.value;
  }

  get isSchoolAdmin(): boolean {
    return this._role.value !== 'fachkraft';
  }

  get isSchulgemeinde(): boolean {
    return this._role.value === 'schulgemeinde';
  }

  get isSchuleinheit(): boolean {
    return this._role.value === 'schuleinheit';
  }

  get mySchools(): UserSchoolItem[] {
    return this._mySchools.value;
  }

  get myAuthorities(): UserSchoolAuthorityItem[] {
    return this._myAuthorities.value;
  }

  get firstAuthorityId(): string | null {
    return this._myAuthorities.value[0]?.school_authority?.id ?? null;
  }

  /**
   * True if the eingeloggte user is OWNER of the given school (not just SCHOOL_ADMIN).
   * Used to gate Pro-feature locking: only OWNERs need to upgrade their own free plan;
   * invited admins inherit access from whoever pays for the school.
   */
  isOwnerOfSchool(schoolId: string): boolean {
    return this._mySchools.value.some(
      u => u.school.id === schoolId && (u.access_type as string) === 'OWNER'
    );
  }

  /**
   * True when the user is ONLY an invited admin of organisations owned by
   * someone else — they hold no OWNER access on any school or authority.
   * Such members are covered by the owner's subscription and must not start
   * their own purchase: the backend returns an empty license list and rejects
   * `POST /v1/licenses/orders` with `MEMBER_CANNOT_PURCHASE`. Used to show the
   * «Abos verwaltet der Eigentümer» hint instead of the shop.
   */
  get isInvitedMemberOnly(): boolean {
    const ownsSchool = this._mySchools.value.some(
      u => (u.access_type as string) === 'OWNER'
    );
    const ownsAuthority = this._myAuthorities.value.some(a =>
      hasFullOrganisationAccess(a.access_type)
    );
    const isMember =
      this._mySchools.value.length > 0 || this._myAuthorities.value.length > 0;
    return isMember && !ownsSchool && !ownsAuthority;
  }

  /**
   * True when the user may see the Abonnement tab: plan, invoices, seat usage
   * and the organisation's Rechnungsdetails.
   *
   * Defined as «an organisation resolves for the current selection», not as a
   * list of roles. The two used to be maintained separately and disagreed: the
   * role list admitted `AUTHORITY_ADMIN` (and an `ADMIN` the API never emits),
   * so an invited admin of a paying Schulgemeinde got the tab, the resolver
   * returned null, and the page told them «Kein aktives Abonnement» about an
   * organisation that pays us every year.
   *
   * Deliberately still wider than {@link isInvitedMemberOnly}, which stays as
   * the gate on *starting a purchase*: a co-Super-Admin sees the tab.
   *
   * Known gap: `my-schools` reports a standalone school's co-Super-Admin as
   * `SCHOOL_ADMIN`/`ROOT_SCHOOL_ADMIN`, indistinguishable from an ordinary
   * invited admin, so they get no tab. Authorities have `AUTHORITY_SUPER_ADMIN`
   * for exactly this; schools need the same before the UI can tell them apart.
   */
  get hasSubscriptionAccess(): boolean {
    return this.billingOrganisation !== null;
  }

  /**
   * The organisation that bills for whatever is currently selected in the
   * entity switcher, or null if the user bills for nothing.
   *
   * Billing is an organisation-level fact, so selecting a unit resolves to the
   * organisation above it rather than to the unit: only a Schulgemeinde or a
   * standalone root unit has a billing account at all. Screens showing the
   * result must name the organisation, so the user can see that a unit's page
   * is showing its Schulgemeinde's invoice details.
   *
   * This is the single resolver for the whole app. It previously existed in
   * four places -- here, and three times inside the Abonnement page -- which is
   * how the page came to disagree with itself about which organisation it was
   * looking at.
   */
  get billingOrganisation(): BillingOrganisation | null {
    return this.billingOrganisationFor(this._activeContext.value);
  }

  /** {@link billingOrganisation} for an explicit context rather than the active one. */
  billingOrganisationFor(
    ctx: ActiveContext | null
  ): BillingOrganisation | null {
    if (ctx?.type === 'authority') {
      const match = this._myAuthorities.value.find(
        a =>
          a.school_authority.id === ctx.id &&
          hasFullOrganisationAccess(a.access_type)
      );
      return match ? { kind: 'authority', id: ctx.id } : null;
    }

    if (ctx?.type === 'school') {
      const item = this._mySchools.value.find(s => s.school.id === ctx.id);
      if (item && AUTHORITY_DERIVED_ACCESS.includes(item.access_type)) {
        // Reached through a Schulgemeinde, so the Schulgemeinde pays.
        //
        // `UserSchoolItem` carries no `school_authority_id`, so we cannot map a
        // unit to its specific authority and fall back to the one the user has
        // full access to. That is exact for everyone today; it breaks only for
        // a user with full access to two Schulgemeinden, which the backend does
        // not yet support anyway (see the multi-organisation follow-up).
        return this.ownedOrganisation();
      }
      if (item && hasFullOrganisationAccess(item.access_type)) {
        // A standalone unit bills for itself; a sub-school bills through its root.
        return {
          kind: 'school',
          id: this.rootSchoolId(
            item.school,
            this._mySchools.value.map(u => u.school)
          ),
        };
      }
      // An invited SCHOOL_ADMIN of someone else's school, or a school we do not
      // administer at all. Falling through to `ownedOrganisation()` here named
      // an unrelated organisation the user happens to own: an admin seat bought
      // for school S was then billed to -- and granted on -- school T.
      //
      // Nothing bills for this selection, and saying so is what makes the
      // callers show «managed by the owner» instead of the wrong organisation.
      return null;
    }

    return this.ownedOrganisation();
  }

  /**
   * {@link billingOrganisationFor} given only a school id.
   *
   * Purchase dialogs know which school a seat or a posting is for, but not
   * whether that school is a standalone organisation, a unit inside a
   * Schulgemeinde or a sub-school. The distinction matters because
   * `GET/PUT /v1/schools/{id}/billing-profile` accepts only the first kind.
   */
  billingOrganisationForSchool(schoolId: string): BillingOrganisation | null {
    return this.billingOrganisationFor({
      type: 'school',
      id: schoolId,
      name: '',
    });
  }

  /**
   * The organisation this user bills for irrespective of what is selected.
   * An owned Schulgemeinde wins: it bills for its whole tree, so its units must
   * never order on their own account.
   */
  private ownedOrganisation(): BillingOrganisation | null {
    const authority = this._myAuthorities.value.find(a =>
      hasFullOrganisationAccess(a.access_type)
    );
    if (authority) {
      return { kind: 'authority', id: authority.school_authority.id };
    }

    const ownedSchool = this._mySchools.value.find(s =>
      hasFullOrganisationAccess(s.access_type)
    );
    return ownedSchool ? { kind: 'school', id: ownedSchool.school.id } : null;
  }

  /** True if the user may administer this Schulgemeinde (Träger). */
  hasAuthorityAdminAccessFor(authorityId: string): boolean {
    return this._myAuthorities.value.some(
      a =>
        a.school_authority.id === authorityId &&
        (hasFullOrganisationAccess(a.access_type) ||
          a.access_type === 'AUTHORITY_ADMIN')
    );
  }

  get activeContext(): ActiveContext | null {
    return this._activeContext.value;
  }

  /**
   * Entity switcher only updates {@link activeContext}. Nav layout stays tied to
   * {@link role} from {@link loadRole} (API), so Schulgemeinde admins still see
   * «Meine Organisation» (Struktur, Träger-Profil, Inserate) when a Schuleinheit
   * is selected in the dropdown.
   */
  switchContext(ctx: ActiveContext): void {
    const current = this._activeContext.value;
    this._contextIsFallback = false;
    if (current && current.type === ctx.type && current.id === ctx.id) {
      return;
    }
    this.publishContext(ctx);
  }

  /**
   * For pages whose URL names no context but whose data does (`/jobs/:id` → the job's
   * owner). Until {@link adoptFallbackContext} or {@link releasePageContext},
   * `loadRole()` leaves the context unset instead of applying its default.
   */
  awaitPageContext(): void {
    this._pageContextPending = true;
    this._pageOwner = null;
    // With a context already set there is nothing to hide.
    if (!this._activeContext.value) this._awaitingPageSig.set(true);
  }

  /**
   * Use the page's owner (`null` if unknown) as context, unless the user or a URL already
   * chose one. Falls back to `loadRole()`'s default when the user doesn't manage `owner`.
   */
  adoptFallbackContext(owner: ContextOwner | null): void {
    this._pageContextPending = false;
    this._pageOwner = owner;
    if (!this._loaded) return; // loadRole() applies `_pageOwner`
    if (this._activeContext.value && !this._contextIsFallback) return;
    const ctx = owner ? this.contextFor(owner) : null;
    if (ctx) {
      this.switchContext(ctx);
    } else if (!this._activeContext.value) {
      this.applyDefaultContext();
    }
  }

  /** The page left before resolving its owner; apply the default if still unset. */
  releasePageContext(): void {
    this._pageContextPending = false;
    this._pageOwner = null;
    this._awaitingPageSig.set(false);
    if (this._loaded && !this._activeContext.value) {
      this.applyDefaultContext();
    }
  }

  private contextFor(owner: ContextOwner): ActiveContext | null {
    if (owner.type === 'authority') {
      const a = this._myAuthorities.value.find(
        x => x.school_authority.id === owner.id
      )?.school_authority;
      return a ? { type: 'authority', id: a.id, name: a.name } : null;
    }
    const s = this._mySchools.value.find(x => x.school.id === owner.id)?.school;
    return s ? { type: 'school', id: s.id, name: s.name ?? '' } : null;
  }

  /** First authority (Schulgemeinde) or first school (Schuleinheit). */
  private applyDefaultContext(): void {
    this._awaitingPageSig.set(false);
    const role = this._role.value;
    const authorities = this._myAuthorities.value;
    const schools = this._mySchools.value;
    if (role === 'schulgemeinde' && authorities.length > 0) {
      const a = authorities[0].school_authority;
      this._contextIsFallback = true;
      this.publishContext({ type: 'authority', id: a.id, name: a.name });
    } else if (role === 'schuleinheit' && schools.length > 0) {
      const s = schools[0].school;
      this._contextIsFallback = true;
      this.publishContext({ type: 'school', id: s.id, name: s.name });
    }
  }

  /**
   * Clears Schulgemeinde/Schuleinheit UI context so the next {@link loadRole} can
   * auto-select `activeContext` from fresh `GET /v1/schools/my-schools` data
   * (e.g. after Schuleinheit onboarding creates a school).
   */
  resetActiveContext(): void {
    this.publishContext(null);
  }

  loadRole(): Observable<UserRole> {
    return forkJoin({
      schools: this.schoolApi.getMySchools().pipe(catchError(() => of([]))),
      authorities: this.schoolAuthorityApi
        .getMySchoolAuthorities()
        .pipe(catchError(() => of([]))),
    }).pipe(
      map(({ schools, authorities }) => {
        this.publishSchools(schools);
        this.publishAuthorities(authorities);

        const role = resolveUserRole(schools, authorities);

        this.publishRole(role);
        this._loaded = true;

        if (this._contextUnverified) {
          this._contextUnverified = false;
          const ctx = this._activeContext.value;
          const verified = ctx ? this.contextFor(ctx) : null;
          // Not accessible to this user: drop it and pick a context below.
          if (!verified) this.publishContext(null);
          else if (verified.name !== ctx?.name) this.publishContext(verified);
        }

        // Auto-set initial active context only if user hasn't already switched.
        // A page's owner wins over the default; while the page is loading, wait.
        if (!this._activeContext.value) {
          const pageCtx = this._pageOwner
            ? this.contextFor(this._pageOwner)
            : null;
          if (pageCtx) {
            this._contextIsFallback = false;
            this.publishContext(pageCtx);
          } else if (!this._pageContextPending) {
            this.applyDefaultContext();
          }
        }

        return role;
      })
    );
  }

  get loaded(): boolean {
    return this._loaded;
  }

  /**
   * Root school id that a Schuleinheit order/upgrade should attach to, or null
   * if the user owns no Schuleinheit school yet (then onboarding legitimately
   * creates the first one).
   *
   * Single source of truth so every entry into the order dialog attaches the
   * subscription to the existing school instead of attempting to create a
   * duplicate — the latter fails the backend `ensure_can_create_school` check
   * with 403 once the school-unit creation limit is reached, which previously
   * pushed users into re-ordering (and re-purchasing) repeatedly.
   */
  getSchuleinheitUpgradeSchoolId(): string | null {
    if (this._role.value !== 'schuleinheit') {
      return null;
    }
    const items = this._mySchools.value;
    if (items.length === 0) {
      return null;
    }
    const all = items.map(u => u.school);
    const ctx = this._activeContext.value;
    const picked =
      (ctx?.type === 'school'
        ? items.find(u => u.school.id === ctx.id)?.school
        : undefined) ?? items[0]?.school;
    if (!picked) {
      return null;
    }
    return this.rootSchoolId(picked, all);
  }

  /** Walk `parent_id` to the root school within the administered list. */
  private rootSchoolId(
    school: UserSchoolItem['school'],
    all: UserSchoolItem['school'][]
  ): string {
    const byId = new Map(all.map(s => [s.id, s]));
    let cur: UserSchoolItem['school'] | undefined = school;
    const visited = new Set<string>();
    while (cur?.parent_id) {
      if (visited.has(cur.id)) break;
      visited.add(cur.id);
      const parent = byId.get(cur.parent_id);
      if (!parent) break;
      cur = parent;
    }
    return cur?.id ?? school.id;
  }
}
